// Lógica pura del wizard de creación de eventos (sin React) — testeable.

export type WizardTicket = { id: string; name: string; price: string; total: string; isFree: boolean };

export type WizardAdvanced = {
  capacity: string; // vacío = suma de entradas
  waitingRoomEnabled: boolean;
  waitingRoomThreshold: string; // vacío = automático
  salesStart: string; // datetime-local, vacío = desde publicación
  salesEnd: string; // vacío = hasta inicio del evento
  maxPerUser: string;
  description: string;
  category: string;
  tags: string;
  refundPolicy: "no_refund" | "until_7_days" | "until_48h" | "flexible";
  collectAttendeeData: boolean;
};

export type WizardState = {
  step: 1 | 2 | 3;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  isVirtual: boolean;
  location: string;
  city: string;
  virtualUrl: string;
  imageUrl: string;
  tickets: WizardTicket[];
  advanced: WizardAdvanced;
  tenantId: string;
};

export const DEFAULT_TIMEZONE = "America/Costa_Rica";

export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

export function defaultWizardState(id: () => string = () => crypto.randomUUID()): WizardState {
  return {
    step: 1,
    title: "",
    date: "",
    startTime: "20:00",
    endTime: "23:00",
    timezone: DEFAULT_TIMEZONE,
    isVirtual: false,
    location: "",
    city: "",
    virtualUrl: "",
    imageUrl: "",
    tickets: [{ id: id(), name: "General", price: "", total: "100", isFree: false }],
    advanced: {
      capacity: "",
      // La sala de espera viene encendida por defecto en todo evento nuevo.
      waitingRoomEnabled: true,
      waitingRoomThreshold: "",
      salesStart: "",
      salesEnd: "",
      maxPerUser: "4",
      description: "",
      category: "Música",
      tags: "",
      refundPolicy: "no_refund",
      collectAttendeeData: false,
    },
    tenantId: "",
  };
}

export function totalTickets(tickets: WizardTicket[]): number {
  return tickets.reduce((s, t) => s + (parseInt(t.total) || 0), 0);
}

/**
 * Umbral automático de la sala de espera: cantidad de compradores simultáneos
 * a partir de la cual se activa la cola. 10% del aforo, entre 50 y 500.
 */
export function autoQueueThreshold(capacity: number): number {
  if (!capacity || capacity <= 0) return 500;
  return Math.min(500, Math.max(50, Math.round(capacity * 0.1)));
}

export type StepErrors = Record<string, string>;

export function validateStep1(s: WizardState): StepErrors {
  const e: StepErrors = {};
  if (!s.title.trim()) e.title = "Poné un nombre para tu evento";
  if (!s.date) e.date = "Elegí la fecha";
  if (s.endTime && s.startTime && s.endTime <= s.startTime && s.endTime > "06:00")
    e.endTime = "La hora de fin debe ser posterior al inicio";
  if (s.isVirtual) {
    if (!/^https?:\/\/\S+$/.test(s.virtualUrl.trim())) e.virtualUrl = "Pegá un enlace válido (https://…)";
  } else {
    if (!s.location.trim()) e.location = "Indicá el lugar";
    if (!s.city.trim()) e.city = "Indicá la ciudad";
  }
  return e;
}

export function validateTickets(tickets: WizardTicket[]): StepErrors {
  const e: StepErrors = {};
  tickets.forEach((t) => {
    if (!t.name.trim()) e[`${t.id}.name`] = "Poné un nombre";
    if (!t.isFree && (t.price === "" || !(parseFloat(t.price) > 0)))
      e[`${t.id}.price`] = "Ingresá un precio o marcala como gratis";
    if (!(parseInt(t.total) > 0)) e[`${t.id}.total`] = "Indicá cuántas hay disponibles";
  });
  return e;
}
